import { Button } from '@daily/shared/components/Button';
import { CardContent } from '@daily/shared/components/Card';
import { Popover } from '@daily/shared/components/Popover';
import { Text } from '@daily/shared/components/Text';
import { useResize } from '@daily/shared/hooks/useResize';
import { isElementVisibleInViewport } from '@daily/shared/lib/dom';
import { pxToRem } from '@daily/shared/lib/pxToRem';
import { useLocalSessionId } from '@daily-co/daily-react';
import React, { memo, useCallback, useState } from 'react';

import { availableReactionEmoji } from '/features/emoji-reactions/hooks/useEmojiReactions';
import { useEmojiSkinTone } from '/hooks/useEmojiSkinTone';

import { EmojiReactions } from './EmojiReactions';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'left' | 'center' | 'right';
  'aria-labelledby': string;
  id: string;
  onClickReaction(reaction: string): void;
  onClose?(): void;
}

export const EmojiReactionsMenu: React.FC<React.PropsWithChildren<Props>> =
  memo(({ align = 'left', onClickReaction, onClose = null, ...props }) => {
    const localSessionId = useLocalSessionId();
    const [ariaLabelledBy, setAriaLabelledBy] = useState<string>(
      props['aria-labelledby']
    );

    useResize(
      useCallback(() => {
        const trayButton = document.getElementById(props['aria-labelledby']);

        setAriaLabelledBy(
          isElementVisibleInViewport(trayButton)
            ? props['aria-labelledby']
            : 'tray-more-btn'
        );
      }, [props])
    );

    const { addModifier } = useEmojiSkinTone();

    return (
      <Popover
        id={props.id}
        {...props}
        align={align}
        placement="top"
        onClose={onClose}
        aria-labelledby={ariaLabelledBy}
        cardProps={{
          noBorder: true,
          spacing: 8,
          shadow: 'strong',
        }}
      >
        <div className="reactions-menu-content" {...props}>
          <CardContent>
            {availableReactionEmoji.map((reaction) => {
              const audienceReaction = addModifier(reaction);
              return (
                <Button
                  className="reaction"
                  key={audienceReaction}
                  onClick={() => onClickReaction(audienceReaction)}
                  variant="ghost"
                >
                  <div className="reactions-menu-button-reactions">
                    <EmojiReactions
                      animation="local"
                      emojiSize={25}
                      exclusiveEmoji={audienceReaction}
                      sessionIds={[localSessionId]}
                    />
                  </div>
                  <Text El="span" variant="large">
                    {audienceReaction}
                  </Text>
                </Button>
              );
            })}
          </CardContent>
        </div>
        <style jsx>{`
          .reactions-menu-content :global(.card-content) {
            background: var(--menu-bg);
            border-radius: 8px;
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 4px;
            height: auto;
            width: auto;
          }
          .reactions-menu-content :global(.reaction) {
            display: inline-flex;
            justify-content: center;
            padding: 8px;
            position: relative;
            user-select: none;
          }
          .reactions-menu-content {
            overflow: hidden; // don't allow emoji to escape their menu containment zone
          }
          .reactions-menu-content :global(.reaction span) {
            font-size: ${pxToRem(25)};
          }
          .reactions-menu-content .reactions-menu-button-reactions {
            left: 50%;
            pointer-events: none;
            position: absolute;
            top: 50%;
            transform: translate(50%, 50%);
          }
        `}</style>
      </Popover>
    );
  });
EmojiReactionsMenu.displayName = 'EmojiReactionsMenu';
